import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { Alert, Text } from '@launchpad-ui/components';
import clsx from 'clsx';

import { MultiItemDisplay, type Props as MultiItemProps } from '../../MultiItemDisplay';

import { Skeleton } from './Skeleton';
import { Source, type Props as SourceProps } from './Source';

import styles from './source.module.css';

export const Sources = memo(
  ({
    sources,
    loading,
    error,
    fallback,
    variant = 'normal',
    behavior = 'tooltip',
    max = 2,
    overflowIndicator,
    ...rest
  }: Props) => {
    const resolvedFallback = useMemo(
      () =>
        fallback === undefined ? (
          <span>
            <Message id="metrics.eventSources.emptyFallback" />
          </span>
        ) : (
          fallback
        ),
      [fallback],
    );

    if (loading) {
      return <Skeleton />;
    }
    if (error) {
      return (
        <Alert variant="inline" status="error">
          <Text>
            <Message id="metrics.eventSources.loadError" />
          </Text>
        </Alert>
      );
    }

    return (
      <MultiItemDisplay
        {...rest}
        max={max}
        list={sources}
        toolTipOverflow
        behavior={behavior}
        fallback={resolvedFallback}
        overflowIndicator={overflowIndicator}
        renderItem={(source) => <Source key={source} variant={variant} sourceKey={source} />}
        toolTipChildren={
          <div className={clsx(styles.allSources, styles[behavior])}>
            <div>
              {sources.slice(max).map((source) => (
                <Source key={source} sourceKey={source} variant={variant} />
              ))}
            </div>
          </div>
        }
      />
    );
  },
);

export interface Props
  extends
    Pick<MultiItemProps<string>, 'max' | 'overflowIndicator' | 'behavior' | 'triggerClassName'>,
    Pick<SourceProps, 'variant'> {
  error?: boolean;
  sources: string[];
  loading?: boolean;
  fallback?: ReactNode;
}
